import styled from 'styled-components';
import { ReactComponent as InspectIcon } from '@elementary/common-ui/assets/24x24/inspect.svg';
import { ReactComponent as AddTestIcon } from '@elementary/common-ui/assets/add-test.svg';
import { ReactComponent as ColumnsIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as TablesIcon } from '@elementary/common-ui/assets/tables.svg';
import { Button, InternalLink, Menu } from '@elementary/common-ui/components';
import { Props as ButtonProps } from '@elementary/common-ui/components/Button/Button';
import type { Option } from '@elementary/common-ui/components/Menu/Menu';
import { Typography } from '@mui/material';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import { WizardMode } from 'testManagement/types';

const TooltipText = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.common.white};
`;

const DisabledTooltip = () => (
  <TooltipText>
    <InternalLink to="/settings/environments">Connect</InternalLink> your code
    repository to add tests directly from the UI.
  </TooltipText>
);

const ColumnsErrorAlert = () => (
  <TooltipText sx={{ padding: 0.5 }}>
    Column information could not be retrieved.
  </TooltipText>
);

type Props = {
  onChange: (item: WizardMode) => void;
  size?: 'medium' | 'large';
  buttonProps?: ButtonProps;
  disableTableTests?: boolean;
  disableColumnTests?: boolean;
  disableCustomQueryTests?: boolean;
};

const AddTestMenuButton = ({
  onChange,
  size = 'medium',
  buttonProps = {},
  disableTableTests,
  disableColumnTests,
  disableCustomQueryTests,
  ...menuProps
}: Props) => {
  const selectedEnvId = useSelectedEnv();

  const { shouldConnectCodeRepo } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });

  const addTestMenuOptions: (Omit<Option, 'value'> & { value: WizardMode })[] =
    [
      {
        value: 'tables',
        label: 'Table test',
        icon: <TablesIcon width={20} />,
        disabled: disableTableTests,
        tooltip: shouldConnectCodeRepo ? 'Only cloud tests can be added.' : '',
      },
      {
        value: 'columns',
        label: 'Column test',
        icon: <ColumnsIcon width={20} />,
        disabled: shouldConnectCodeRepo || disableColumnTests,
        tooltip: shouldConnectCodeRepo ? (
          <DisabledTooltip />
        ) : disableColumnTests ? (
          <ColumnsErrorAlert />
        ) : null,
      },
      {
        value: 'customQuery',
        label: 'Custom query test',
        icon: <InspectIcon width={20} />,
        disabled: shouldConnectCodeRepo || disableCustomQueryTests,
        tooltip: shouldConnectCodeRepo && <DisabledTooltip />,
      },
    ];

  return (
    <Menu
      // medium size has height 40, which causes the header to jump
      menuButton={
        <Button size={size} startIcon={<AddTestIcon />} {...buttonProps}>
          Add test
        </Button>
      }
      options={addTestMenuOptions}
      closeOnChange
      onChange={(item) => onChange(item as WizardMode)}
      {...menuProps}
    />
  );
};

export default AddTestMenuButton;
